import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Flex,
  message,
  Modal,
  Table,
  Tag,
  Typography,
} from 'antd';
import { EditOutlined, UserOutlined } from '@ant-design/icons';

import PageTitle from '../../components/PageTitle';
import IntlMessage from '../../components/IntlMessage';
import _, { debounce } from 'lodash';
import busOrdersApi from '../../api/bus_orders';
import { getTagPaymentStatus, paymentStatus, payMethod } from './payment.type';
import { getTagOrderStatus, orderStatus } from './order_status.type';
import SearchBusOrder from './SearchBusOrder';
import operatorsApi from '../../api/operators';
import DetailBusOrder from './GetDetailBusOrder';
import ActionBar from '../../components/ActionBar';
import BusOrderForm from './create/BusOrderForm';
import useTime from '../../hooks/useTime';
import BookingStatusForm from './BookingStatusForm';
import moment from 'moment';
import FilterBar from '../../components/FilterBar';
import { OrderStatus } from '../../utils/constant';
import EditBusOrder from './\bEditBusOrder';
import { formatCurrencyCustom } from '../../utils/helpers';
import { useIntl } from 'react-intl';
import reportApi from '../../api/report';

const { Text } = Typography;

const BusOrder = () => {
  const [loading, setLoading] = useState(false);
  const [busOrders, setBusOrders] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [total, setTotal] = useState(1);
  const [operator, setOperator] = useState('');
  const [condition, setCondition] = useState({});
  const [currentData, setCurrentData] = useState(null);
  const [isModalForm, setIsModalForm] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalBookingStatus, setIsModalBookingStatus] = useState(false);
  const [isModalSmsStatus, setIsModalSmsStatus] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    order_by: 'id',
    order_dir: 'DESC',
    keyword: '',
  });
  const { convertToAnyTimezone } = useTime();

  const filterOperator = {
    page: 1,
    limit: 10,
    order_by: 'id',
    order_dir: 'ASC',
  };
  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const getBusOrders = await busOrdersApi.getAll(filter);
        let operator = await operatorsApi.getAll(filterOperator);
        setOperator(operator.data);
        setBusOrders(getBusOrders.data);
        setTotal(getBusOrders.total);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [filter, refresh]);

  useEffect(() => {
    if (filter?.filter_name && filter?.filter_value) {
      const newCondition = _.zipObjectDeep(
        filter?.filter_name,
        filter?.filter_value,
      );
      setCondition(newCondition);
    }
  }, [filter]);

  const onDelete = async (id) => {
    try {
      const res = await busOrdersApi.delete(id);
      if (res) {
        message.success('Successfully deleted booking');
        onRefresh();
        setSelectedRowKeys([]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const onOpenDetail = (record) => {
    setIsModalOpen(true);
    setCurrentData(record);
  };

  const onOpenStatusChange = (record) => {
    setIsModalBookingStatus(true);
    setCurrentData(record);
  };

  const onModalClose = (record) => {
    setCurrentData('');
    setIsModalOpen(false);
  };

  const onModalFormClose = () => {
    setIsModalForm(false);
  };

  const onCloseModalBookingStatus = () => {
    setIsModalBookingStatus(false);
  };

  const onOpenSmsChange = (record) => {
    setIsModalSmsStatus(true);
    setCurrentData(record);
  };

  const onCloseModalSmsStatus = () => {
    setIsModalSmsStatus(false);
  };

  const [cashier, setCashier] = useState([]);
  const [filterCashier, setFilterCashier] = useState({
    page: 1,
    limit: 40,
    order_by: 'firstname',
    order_dir: 'ASC',
  });
  useEffect(() => {
    async function fetchData() {
      const res = await reportApi.getCashierReportCustomers(filterCashier);
      if (res) {
        const allUsers = [...(res.cashiers || []), ...(res.operators || [])];
        setCashier(
          allUsers
            .filter((item) => item.user_id)
            .map((item) => ({
              value: item.user_id,
              label: `${item.firstname || ''} ${item.lastname || ''}`.trim(),
            })),
        );
      } else {
        setCashier([]);
      }
    }

    fetchData();
  }, [filterCashier]);

  const columns = [
    {
      title: <IntlMessage id="global.order_no" />,
      key: 'order_no',
      dataIndex: 'order_no',
      width: 100,
      render: (text, record, index) => {
        return (
          <Button
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              color: '#14bb8c',
            }}
            onClick={() => onOpenDetail(record)}
            type="link">
            {record.order_no}
            <UserOutlined />
          </Button>
        );
      },
    },
    {
      title: <IntlMessage id="global.route" />,
      key: 'route',
      width: 350,
      dataIndex: 'route_id',
      render: (text, record, index) => {
        return record?.order_items ? (
          <>
            {record.order_items.map((item, index) => (
              <Flex>
                <Text key={index}>
                  {item?.segment?.start_stop?.title} -{' '}
                  {item?.segment?.end_stop?.title}
                </Text>
              </Flex>
            ))}
          </>
        ) : null;
      },
    },
    {
      title: <IntlMessage id="global.operator" />,
      key: 'operator_id',
      width: 300,
      dataIndex: 'operator_id',
      render: (text, record, index) => {
        return record?.route?.operator?.title
          ? `${record?.route?.operator?.title}`
          : null;
      },
    },

    {
      title: <IntlMessage id="orders.start_date" />,
      key: 'start_date',
      sorter: true,
      width: 250,
      dataIndex: 'date',
      render: (text, record, index) => {
        return (
          <Text>{convertToAnyTimezone(record?.date).format('DD/MM/YYYY')}</Text>
        );
      },
    },
    {
      title: <IntlMessage id="orders.total" />,
      key: 'total',
      sorter: true,
      width: 250,
      dataIndex: 'total',
      render: (text, record, index) => {
        return (
          <>
            <Text>
              {formatCurrencyCustom(
                +record.subtotal,
                JSON.parse(
                  record?.order_items?.[0]?.segment?.route?.operator?.params ||
                    '{}',
                ),
              )}
            </Text>
            <br />
            <Text>
              {record?.pay_method}
              {record?.payment_method_traffic}
            </Text>
          </>
        );
      },
    },
    {
      title: <IntlMessage id="global.payment_status" />,
      key: 'pay_status',
      sorter: true,
      width: 300,
      dataIndex: 'pay_status',
      render: (text, record, index) => {
        const expiresAt = moment(record?.expires_at);
        const now = moment();
        const diff = moment.duration(expiresAt.diff(now));
        const formattedDiff = `${diff
          .minutes()
          .toString()
          .padStart(2, '0')}:${diff.seconds().toString().padStart(2, '0')}`;

        return (
          <>
            <Flex justify="center">{getTagPaymentStatus(record)} </Flex>
            <Text>
              {(record?.order_status === 'pending' &&
                record?.expires_at !== null) ||
              (record?.pay_status === 'pending' && record?.expires_at !== null)
                ? `Expires after: ${formattedDiff}`
                : null}
            </Text>
          </>
        );
      },
    },
    {
      title: <IntlMessage id="orders.order_status" />,
      key: 'order_status',
      sorter: true,
      width: 300,
      dataIndex: 'order_status',
      render: (text, record, index) => {
        return (
          <>
            <Flex justify="center">{getTagOrderStatus(record)}</Flex>
          </>
        );
      },
    },
    {
      title: <IntlMessage id="global.created_by" />,
      key: 'status',
      width: 250,
      dataIndex: 'created_by',
      render: (text, record, index) => {
        return record?.created_by?.fullname && record?.created_at ? (
          <>
            {`${record?.created_by?.fullname} ${convertToAnyTimezone(
              record?.created_at,
            ).format('DD/MM/YYYY HH:mm')} `}
            <br />
            <Text>Booking id: {record?.id}</Text>
          </>
        ) : (
          ''
        );
      },
    },
    {
      title: <IntlMessage id="global.app_id" />,
      key: 'params',
      width: 50,
      dataIndex: 'params',
      render: (text, record, index) => {
        return record.params ? JSON.parse(record.params)?.app?.id : null;
      },
    },
    {
      title: <IntlMessage id="global.action" />,
      key: 'created_at',
      width: 120,
      dataIndex: 'created_at',
      render: (text, record, index) => {
        return (
          <Flex justify="center" vertical={true} gap={10}>
            <Button
              type="primary"
              onClick={() => onOpenStatusChange(record)}
              size="small">
              <IntlMessage id="global.edit" />
            </Button>
            {record.order_status === 'confirmed' &&
              record.pay_status === 'pending' && (
                <Button
                  type="primary"
                  onClick={() => onOpenSmsChange(record)}
                  size="small">
                  <IntlMessage id="global.sms_payment" />
                </Button>
              )}
          </Flex>
        );
      },
    },
  ];

  const getOrder = (order) => {
    if (order === 'ascend') return 'ASC';
    if (order === 'descend') return 'DESC';
    return 'DESC';
  };

  const onChangeTable = (pagination, filters, sorter) => {
    const currentFilter = {
      ...filter,
      page: pagination.current,
      limit: pagination.pageSize,
    };

    if (sorter) {
      const { field, order } = sorter;
      if (field === 'operator.short_name') {
        currentFilter.order_by = 'operator';
      } else if (field === 'payment_method.method') {
        currentFilter.order_by = 'payment_method';
      } else {
        currentFilter.order_by = field || 'id';
      }

      currentFilter.order_dir = getOrder(order);

      setFilter(currentFilter);
    }

    if (pagination.pageSize !== filter.page) {
      setBusOrders([]);
    }
  };

  const onFilter = debounce((value, name, type) => {
    if (type === 'search') {
      setFilter((filter) => {
        return { ...filter, keyword: value, page: 1 };
      });
    } else {
      const filter_obj = { ...condition, [name]: value };
      let filter_name = [];
      let filter_value = [];
      for (var key in filter_obj) {
        if (filter_obj[key]) {
          filter_name.push(key);
          filter_value.push(filter_obj[key]);
        }
      }
    }
  }, 300);

  const onRefresh = () => {
    setRefresh((refresh) => !refresh);
  };

  const onCopy = async () => {
    if (selectedRowKeys.length > 0) {
      for (const key of selectedRowKeys) {
        await busOrdersApi.copy(key).catch((err) => {
          console.log(err);
        });
      }
      message.success('copy success');
      onRefresh();
    }
  };

  const showModal = () => {
    setIsModalForm(true);
  };
  const intl = useIntl();
  const onExport = async () => {
    setLoading(true);
    await busOrdersApi
      .exportToExcel(filter)
      .then(() => {})
      .catch((error) => {
        console.error(error);
        Modal.error({
          title: error.data.message,
          okText: intl.formatMessage({ id: 'global.close' }),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <React.Fragment>
      <Flex justify="space-between" align="center">
        <Col>
          <PageTitle title={<IntlMessage id="global.orders" />} />
        </Col>
        <Col>
          <ActionBar
            isShowAddButton={true}
            isShowCopyButton={false}
            onAdd={showModal}
            isShowDeleteButton={true}
            // onCopy={onCopy}
            deleteMessage={
              'Warning! You are about to delete this booking and its associated records. Are you sure you want to delete this record?'
            }
            isShowPublishButtons={false}
            isShowExportButton={true}
            onExport={() => onExport()}
            onDelete={() => onDelete(selectedRowKeys)}
            isDisabled={selectedRowKeys.length > 0 ? false : true}
            onRefresh={onRefresh}
          />
        </Col>
      </Flex>

      <Card bordered={false}>
        <Flex vertical gap={8}>
          {/* searchbar */}
          <FilterBar
            onFilter={onFilter}
            playholderSearch="Référence/Téléphone"
          />
          <SearchBusOrder
            onFilter={onFilter}
            filter={filter}
            setFilter={setFilter}
            data={[
              {
                name: 'operator_id',
                data: operator,
                placeholder: <IntlMessage id="global.operator" />,
              },
              {
                name: 'order_status',
                data: orderStatus,
                placeholder: <IntlMessage id="orders.order_status" />,
              },
              {
                name: 'pay_status',
                data: paymentStatus,
                placeholder: <IntlMessage id="global.payment_status" />,
              },
              {
                name: 'pay_method',
                data: payMethod,
                placeholder: <IntlMessage id="global.payment_method" />,
              },
              {
                name: 'created_by',
                data: cashier,
                placeholder: <IntlMessage id="global.cashier" />,
              },
            ]}
          />
          <Table
            columns={columns}
            tableLayout="auto"
            pagination={{
              limit: filter.limit,
              current: filter.page,
              total: total,
              showSizeChanger: true,
            }}
            size="small"
            onChange={onChangeTable}
            dataSource={busOrders}
            rowSelection={rowSelection}
            loading={loading}
            scroll={{ x: 1200 }}
            rowKey={'id'}
          />
        </Flex>
      </Card>
      {isModalOpen && (
        <DetailBusOrder
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          currentData={currentData}
          onModalClose={onModalClose}
        />
      )}
      {isModalForm ? (
        <BusOrderForm
          isModalForm={isModalForm}
          setIsModalForm={setIsModalForm}
          onModalFormClose={onModalFormClose}
          // currentData={currentData}
          onRefresh={onRefresh}
        />
      ) : null}

      {isModalSmsStatus ? (
        <BookingStatusForm
          isModalBookingStatus={isModalSmsStatus}
          setIsModalBookingStatus={setIsModalSmsStatus}
          onCloseModalBookingStatus={onCloseModalSmsStatus}
          currentData={currentData}
          onRefresh={onRefresh}
        />
      ) : null}

      {isModalBookingStatus ? (
        <EditBusOrder
          isModalBookingStatus={isModalBookingStatus}
          setIsModalBookingStatus={setIsModalBookingStatus}
          onCloseModalBookingStatus={onCloseModalBookingStatus}
          currentData={currentData}
          onRefresh={onRefresh}
        />
      ) : null}
    </React.Fragment>
  );
};

export default BusOrder;
